import { motion } from 'framer-motion';
import { Target, Users, Award, Wrench, Phone, Calendar } from 'lucide-react';

export const AboutSection = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To deliver the highest quality workmanship and unsurpassed attention to detail, all with the excellent customer service you expect from industry veterans.'
    },
    {
      icon: Users,
      title: '30 Years Experience',
      description: 'Trust 30 years of sealcoating industry experience. Our knowledge ensures your asphalt receives the best possible care.'
    },
    {
      icon: Award,
      title: 'Quality Guaranteed',
      description: 'Our proprietary, long-lasting sealants ensure your asphalt stays protected throughout the year, maintaining both strength and appearance.'
    },
    {
      icon: Wrench,
      title: 'Expert Team',
      description: 'Our experienced crew brings decades of combined expertise to every residential and commercial project.'
    }
  ];

  return (
    <section id="about" className="py-24 bg-neutral-950 relative overflow-hidden">
      {/* Rhino watermark - decorative */}
      <div className="absolute top-0 right-0 opacity-[0.03] pointer-events-none" aria-hidden="true">
        <img src="/images/logo.jpg" alt="" role="presentation" className="w-[600px] h-[600px] object-contain" />
      </div>

      {/* Background texture */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="absolute inset-0 bg-repeat"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section header with logo */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <img src="/images/logo.jpg" alt="Rhino Seal" className="w-16 h-16 rounded-full border-2 border-red-600" />
          </div>
          <span className="text-red-500 text-sm font-semibold uppercase tracking-wider">
            Who We Are
          </span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-4 mb-6">
            BUILT ON TRUST &
            <span className="text-red-500"> DURABILITY</span>
          </h2>
          <p className="text-xl text-neutral-400 max-w-3xl mx-auto">
            At RhinoSeal, trust 30 years of sealcoating industry experience. We deliver the highest quality 
            workmanship and unsurpassed attention to detail, all with the excellent customer service you 
            expect from industry veterans.
          </p>
        </motion.div>

        {/* Content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <motion.div 
              className="relative rounded-sm overflow-hidden border-2 border-gray-700"
              whileHover={{ borderColor: '#dc2626', boxShadow: '0 0 30px rgba(220, 38, 38, 0.3)' }}
              transition={{ duration: 0.3 }}
            >
              <img
                src="/images/about-team.webp"
                alt="Professional asphalt crack filling and sealcoating service in Worcester MA - Rhino Seal team applying hot pour crack filler"
                title="Rhino Seal Asphalt Maintenance - Professional Crack Filling Service"
                className="w-full h-[400px] object-cover"
                loading="lazy"
                width="600"
                height="400"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </motion.div>

            {/* Floating badge with rhino */}
            <motion.div
              className="absolute -bottom-6 -right-6 bg-red-600 text-white p-6 rounded-sm shadow-xl"
              animate={{ y: [0, -5, 0] }}
              transition={{ repeat: Infinity, duration: 3 }}
              whileHover={{ scale: 1.1, boxShadow: '0 0 25px rgba(220, 38, 38, 0.6)' }}
            >
              <div className="flex items-center gap-3">
                <img src="/images/logo.jpg" alt="Rhino Seal Logo" className="w-10 h-10 rounded-full border-2 border-white" />
                <div>
                  <div className="text-3xl font-black font-mono">30</div>
                  <div className="text-sm font-bold uppercase">Years Strong</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Text content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h3 className="text-3xl font-black text-white">
              THE <span className="text-red-500">RHINO SEAL</span> DIFFERENCE
            </h3>
            
            <p className="text-neutral-300 leading-relaxed">
              Our proprietary, long-lasting sealants ensure your asphalt stays protected throughout the year, 
              maintaining both its strength and appearance in most weather conditions.
            </p>

            <p className="text-neutral-300 leading-relaxed">
              Take a look at our project gallery for proof of our dedication to service and the quality of our work. 
              Nothing is more important than your satisfaction, so let the pictures do the talking and know up front 
              that RhinoSeal is the choice for you.
            </p>

            <div className="border-l-4 border-red-600 pl-6 py-4 bg-red-600/10 rounded-r-sm">
              <p className="text-lg text-white font-semibold italic mb-2">
                "Keep your asphalt strong. Give it the Rhino Seal."
              </p>
              <div className="flex items-center gap-2">
                <img src="/images/logo.jpg" alt="Rhino" className="w-6 h-6 rounded-full" />
                <p className="text-red-500 font-semibold">— The Rhino Seal Team</p>
              </div>
            </div>

            {/* Call to action info */}
            <div className="bg-neutral-900 border border-gray-800 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-6 h-6 text-red-500" />
                <h4 className="text-white font-bold">Seasonal Availability</h4>
              </div>
              <p className="text-neutral-400 mb-4">
                Call us from <span className="text-white font-semibold">March to November</span> for whatever you might need. 
                We guarantee a <span className="text-red-500 font-semibold">three business day turnaround</span> on your estimate.
              </p>
              <motion.a
                href="tel:7746967152"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-4 py-2 rounded-sm transition-colors"
                whileHover={{ scale: 1.05 }}
              >
                <Phone className="w-4 h-4" />
                (774) 696-7152
              </motion.a>
            </div>

            <div className="flex flex-wrap gap-4 pt-4">
              <motion.div 
                className="bg-neutral-800 px-4 py-2 rounded-sm border border-gray-700"
                whileHover={{ scale: 1.05, borderColor: '#dc2626', backgroundColor: 'rgba(220, 38, 38, 0.1)' }}
              >
                <span className="text-red-500 font-bold">✓</span>
                <span className="text-white ml-2">Fully Licensed</span>
              </motion.div>
              <motion.div 
                className="bg-neutral-800 px-4 py-2 rounded-sm border border-gray-700"
                whileHover={{ scale: 1.05, borderColor: '#dc2626', backgroundColor: 'rgba(220, 38, 38, 0.1)' }}
              >
                <span className="text-red-500 font-bold">✓</span>
                <span className="text-white ml-2">Fully Insured</span>
              </motion.div>
              <motion.div 
                className="bg-neutral-800 px-4 py-2 rounded-sm border border-gray-700"
                whileHover={{ scale: 1.05, borderColor: '#dc2626', backgroundColor: 'rgba(220, 38, 38, 0.1)' }}
              >
                <span className="text-red-500 font-bold">✓</span>
                <span className="text-white ml-2">Free Estimates</span>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Values grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-neutral-900 border border-gray-800 p-6 rounded-sm"
              whileHover={{ 
                scale: 1.05, 
                borderColor: '#dc2626',
                boxShadow: '0 0 25px rgba(220, 38, 38, 0.2)'
              }}
            >
              <motion.div 
                className="w-12 h-12 bg-red-600/10 rounded-sm flex items-center justify-center mb-4 border border-red-600/30"
                whileHover={{ backgroundColor: 'rgba(220, 38, 38, 0.2)', scale: 1.1 }}
              >
                <value.icon className="w-6 h-6 text-red-500" />
              </motion.div>
              <h4 className="text-xl font-bold text-white mb-2">{value.title}</h4>
              <p className="text-neutral-400 text-sm leading-relaxed">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
